/**
 * <paex-collection-index> — grid layout for "PE - Collection list".
 * Handles: a row limit with a "View more"/"View less" toggle, a vertical A–Z filter,
 * and integration with the section's search bar.
 *
 * Priority of what's shown:
 *   1. Search (>= data-min chars) — matches ALL items, ignores the active letter, no row limit.
 *   2. Active letter — items whose first letter matches, no row limit (auto-expanded).
 *   3. Default — all items, capped to (rows x columns); "View more" reveals the rest.
 * Columns are read from the live grid (auto-fill), so the row limit is accurate at any width.
 */
if (!customElements.get('paex-collection-index')) {
  class PaexCollectionIndex extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('.pe-cl-grid');
      if (!this.grid) return;

      this.items = Array.from(this.grid.children);
      this.moreBtn = this.querySelector('.pe-cl-more');
      this.empty = this.querySelector('.pe-cl-empty');
      this.alphaInner = this.querySelector('.pe-cl-alpha__inner');
      this.letterBtns = Array.from(this.querySelectorAll('.pe-cl-alpha__letter'));
      this.clearBtn = this.querySelector('[data-clear]');

      this.rowsDesktop = parseInt(this.dataset.rowsDesktop, 10) || 5;
      this.moreText = this.dataset.more || 'View more';
      this.min = parseInt(this.dataset.min, 10) || 3;

      this.section = this.closest('.section-collection-list');
      this.searchInput = this.section ? this.section.querySelector('.pe-cl-search__input') : null;
      // Where "All" scrolls to: the section heading (so the search bar below it is fully
      // shown), falling back to this section's own search bar, then the first card.
      this.scrollAnchor =
        (this.section &&
          (this.section.querySelector('.title-wrapper') ||
            this.section.querySelector('.pe-cl-search'))) ||
        null;

      this.activeLetter = null;
      // Once expanded (via "View more" or a letter that opens the full section) the grid
      // stays full-height for the rest of the page session — "All" won't re-condense it,
      // only a page refresh resets this.
      this.expanded = false;

      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => {
          this.expanded = true;
          this.render();
        });
      }

      this.letterBtns.forEach((btn) => {
        if (btn.disabled) return;
        btn.addEventListener('click', () => this.jumpToLetter(btn.dataset.letter));
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.activeLetter = null;
          if (this.searchInput) this.searchInput.value = '';
          // Note: intentionally does NOT collapse — an expanded grid stays expanded.
          this.render();
          // Bring the user to the section heading so the search bar is fully in view.
          const target = this.scrollAnchor || this.items[0];
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      if (this.searchInput) {
        this.searchInput.addEventListener('input', () => this.render());
      }

      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(this.render.bind(this), 150);
      });

      this.render();
    }

    columns() {
      const template = getComputedStyle(this.grid).gridTemplateColumns;
      const count = template.split(' ').filter(Boolean).length;
      return count || 1;
    }

    render() {
      const query = this.searchInput ? this.searchInput.value.trim().toLowerCase() : '';
      const searching = query.length >= this.min;

      // The A–Z letters no longer filter — they scroll to the first matching item — so
      // the only thing that narrows the list is the search bar.
      let eligible;
      if (searching) {
        eligible = this.items.filter((el) => (el.getAttribute('data-search') || '').indexOf(query) !== -1);
      } else {
        eligible = this.items.slice();
      }

      // Row limit only applies in the default (non-search) state on desktop.
      // On mobile the full list is always shown.
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const limited = isDesktop && !searching;
      let visibleCount = eligible.length;

      if (limited) {
        const limit = this.rowsDesktop * this.columns();
        if (!this.expanded) visibleCount = Math.min(eligible.length, limit);
        if (this.moreBtn) {
          // One-way: shown only while collapsed with overflow; disappears once expanded.
          this.moreBtn.hidden = this.expanded || eligible.length <= limit;
          this.moreBtn.textContent = this.moreText;
        }
      } else if (this.moreBtn) {
        this.moreBtn.hidden = true;
      }

      const visible = new Set(eligible.slice(0, visibleCount));
      this.items.forEach((el) => {
        el.hidden = !visible.has(el);
      });

      if (this.empty) this.empty.hidden = eligible.length !== 0;

      this.letterBtns.forEach((btn) => {
        btn.classList.toggle('is-active', !searching && btn.dataset.letter === this.activeLetter);
      });

      this.fitAlpha();
    }

    /**
     * Scrolls to the first item whose name starts with the given letter. On desktop, if
     * that item is beyond the current row limit, the grid expands (as if "View more" was
     * pressed) first so the target is rendered, then we scroll to it.
     */
    jumpToLetter(letter) {
      const target = this.items.find((el) => el.getAttribute('data-letter') === letter);
      if (!target) return;

      // Letters operate on the full list, so drop any active search first.
      if (this.searchInput && this.searchInput.value) this.searchInput.value = '';

      this.activeLetter = letter;

      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      if (isDesktop) {
        const limit = this.rowsDesktop * this.columns();
        if (this.items.indexOf(target) >= limit) this.expanded = true;
      }

      this.render();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /**
     * Sizes the A–Z rail so it never exceeds the visible grid height (no empty space
     * below a short grid) and stays within the viewport, shrinking the letters to fit
     * when few rows are shown — full size is the max. Then pins it vertically centered.
     */
    fitAlpha() {
      if (!this.alphaInner) return;

      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const maxFont = isDesktop ? 13 : 11;
      const baseGap = isDesktop ? 0.2 : 0.4;

      // Start from the max size, then measure and scale down only if needed.
      this.alphaInner.style.setProperty('--pe-alpha-font', maxFont + 'px');
      this.alphaInner.style.setProperty('--pe-alpha-gap', baseGap + 'rem');

      const viewport = window.innerHeight;
      const available = Math.min(this.grid.offsetHeight, viewport) - 4;
      const natural = this.alphaInner.scrollHeight;

      if (natural > available && available > 0) {
        const scale = available / natural;
        this.alphaInner.style.setProperty('--pe-alpha-font', Math.max(7, maxFont * scale) + 'px');
        this.alphaInner.style.setProperty('--pe-alpha-gap', baseGap * scale + 'rem');
      }

      // Center the (now-fitted) rail vertically in the viewport as it sticks.
      const railHeight = this.alphaInner.offsetHeight;
      this.alphaInner.style.top = Math.max(0, (viewport - railHeight) / 2) + 'px';
    }
  }

  customElements.define('paex-collection-index', PaexCollectionIndex);
}
